import { useQuery } from '@tanstack/react-query';
import { Suspense, useMemo, useState } from 'react';

import { BarCompare, LineTrend, ParetoChart } from '../../components/charts';
import { ExportMenu } from '../../components/ExportMenu';
import { FilterBar } from '../../components/FilterBar';
import { Modal } from '../../components/Modal';
import { Select } from '../../components/Select';
import { DataTable } from '../../components/Table';
import {
  getProductDetail,
  getProductSales,
  listCategories,
  type ProductSalesRow,
} from '../../data/fetchers';
import { CHART_H } from '../../lib/chartTokens';
import type { ExportCol } from '../../lib/export';
import {
  fmtAmount,
  fmtBaht,
  fmtBahtShort,
  fmtCompact,
  fmtInt,
  fmtSatangCompact,
} from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';
import { useDateRange } from '../../lib/useDateRange';
import { ReportDateFilter } from './ReportDateFilter';
import { ReportHeader } from './ReportHeader';

type Metric = 'amount' | 'profit' | 'qty';
type Mover = 'all' | 'up' | 'down';

export const deltaFrac = (cur: number, prev: number): number => {
  if (prev !== 0) return (cur - prev) / Math.abs(prev);
  return cur === 0 ? 0 : cur > 0 ? 1 : -1;
};
export const shareValue = (metric: Metric, value: number): number =>
  metric === 'profit' ? Math.max(0, value) : value;
const round1 = (n: number): number => Math.round(n * 10) / 10;
const chartSkeleton = (
  <div
    className="animate-pulse bg-bg-warm rounded-card"
    style={{ height: CHART_H.md }}
  />
);

interface ExportRow {
  rank: number;
  name: string;
  category: string;
  baseUnit: string;
  qty: number;
  netAmountBaht: string;
  netAmountSatang: number;
  cogsBaht: string;
  profitBaht: string;
  marginPct: number | '';
  missingCostLines: number;
  sharePct: number;
  deltaPct: number;
}
const exportCols: ExportCol<ExportRow>[] = [
  { key: 'rank', header: 'อันดับ' },
  { key: 'name', header: 'สินค้า' },
  { key: 'category', header: 'หมวด' },
  { key: 'baseUnit', header: 'หน่วยฐาน' },
  { key: 'qty', header: 'จำนวนหน่วยฐาน' },
  { key: 'netAmountBaht', header: 'ยอดขายสุทธิ' },
  { key: 'netAmountSatang', header: 'ยอดขายสุทธิ (satang)' },
  { key: 'cogsBaht', header: 'ต้นทุนขาย' },
  { key: 'profitBaht', header: 'กำไรขั้นต้น' },
  { key: 'marginPct', header: 'Margin %' },
  { key: 'missingCostLines', header: 'รายการที่ต้นทุนไม่ครบ' },
  { key: 'sharePct', header: 'สัดส่วน %' },
  { key: 'deltaPct', header: 'Δ %' },
];

export function ProductSalesView() {
  const { scope, ready } = useBranchScope();
  const { range, prev } = useDateRange();
  const [metric, setMetric] = useState<Metric>('amount');
  const [cat, setCat] = useState('all');
  const [mover, setMover] = useState<Mover>('all');
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: cats = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
  });
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      'report',
      'product-sales',
      scope.branchId,
      scope.allowedBranchIds,
      range.fromDay,
      range.toDay,
    ],
    queryFn: () => getProductSales(scope, range, prev),
    enabled: ready,
  });

  const catName = useMemo(
    () => Object.fromEntries(cats.map((c) => [c.id, c.name])),
    [cats],
  );
  const valOf = (r: ProductSalesRow) =>
    metric === 'amount' ? r.amount : metric === 'profit' ? r.profit : r.qty;
  const prevOf = (r: ProductSalesRow) =>
    metric === 'amount'
      ? r.prevAmount
      : metric === 'profit'
        ? r.prevProfit
        : r.prevQty;
  const metricLabel =
    metric === 'amount'
      ? 'ยอดขายสุทธิ'
      : metric === 'profit'
        ? 'กำไร'
        : 'จำนวน';
  const valueFmt = metric === 'qty' ? fmtInt : fmtBahtShort;
  const tickFmt = metric === 'qty' ? fmtCompact : fmtSatangCompact;

  const visible = useMemo(() => {
    let rows = (data?.rows ?? []).filter((r) => r.qty > 0 || r.prevQty > 0);
    if (cat !== 'all') rows = rows.filter((r) => r.categoryId === cat);
    if (mover !== 'all')
      rows = rows.filter((r) => {
        const d = deltaFrac(valOf(r), prevOf(r));
        return mover === 'up' ? d > 0.001 : d < -0.001;
      });
    return [...rows].sort((a, b) => valOf(b) - valOf(a));
  }, [data, cat, mover, metric]);

  const totalForShare = useMemo(
    () => visible.reduce((sum, row) => sum + shareValue(metric, valOf(row)), 0),
    [visible, metric],
  );

  // KPIs use the full (unfiltered) result so they read as headline figures.
  const amtDelta = deltaFrac(
    data?.totalAmount ?? 0,
    data?.prevTotalAmount ?? 0,
  );
  const marginRate =
    data && data.totalAmount !== 0 ? data.totalProfit / data.totalAmount : null;
  const activeSkuRate =
    data && data.totalSku !== 0 ? data.activeSku / data.totalSku : 0;

  const pareto = useMemo(() => {
    let cum = 0;
    return visible
      .filter((row) => shareValue(metric, valOf(row)) > 0)
      .slice(0, 8)
      .map((r) => {
        const value = shareValue(metric, valOf(r));
        cum += value;
        return {
          label: r.name.split(' ')[0],
          value,
          cum: totalForShare ? round1((cum / totalForShare) * 100) : 0,
        };
      });
  }, [visible, totalForShare, metric]);

  const topBars = useMemo(
    () => visible.slice(0, 8).map((r) => ({ label: r.name, value: valOf(r) })),
    [visible, metric],
  );

  const exportRows: ExportRow[] = visible.map((r, i) => ({
    rank: i + 1,
    name: r.name,
    category: catName[r.categoryId] ?? r.categoryId,
    baseUnit: r.baseUnitName,
    qty: r.qty,
    netAmountBaht: fmtBaht(r.amount),
    netAmountSatang: r.amount,
    cogsBaht: fmtBaht(r.cogs),
    profitBaht: fmtBaht(r.profit),
    marginPct: r.margin == null ? '' : round1(r.margin * 100),
    missingCostLines: r.missingCostLineCount,
    sharePct: totalForShare
      ? round1((shareValue(metric, valOf(r)) / totalForShare) * 100)
      : 0,
    deltaPct: round1(deltaFrac(valOf(r), prevOf(r)) * 100),
  }));

  return (
    <div className="bo-fade mx-auto w-full max-w-[1480px]">
      <ReportHeader
        title="ยอดขายและกำไรรายสินค้า"
        subtitle="เห็นว่าสินค้าตัวไหนทำยอด ตัวไหนสร้างกำไร และตัวไหนกำลังเปลี่ยนทิศทาง"
        status={{
          label: isLoading
            ? 'กำลังประมวลผล'
            : isError
              ? 'โหลดข้อมูลไม่สำเร็จ'
              : `${visible.length} สินค้า`,
          tone: isLoading ? 'neutral' : isError ? 'danger' : 'ok',
        }}
        actions={
          <ExportMenu
            rows={exportRows}
            cols={exportCols}
            filename="ยอดขายและกำไรรายสินค้า"
            title="ยอดขายและกำไรรายสินค้า"
            pdf
          />
        }
      />
      <ReportDateFilter />

      {isError ? (
        <section
          className="mb-6 rounded-card border border-danger/25 bg-danger-light/35 px-5 py-6 sm:px-7"
          role="alert"
        >
          <div className="max-w-2xl">
            <div className="text-sm-label font-bold uppercase tracking-[0.12em] text-danger">
              โหลดรายงานไม่สำเร็จ
            </div>
            <h2 className="mt-2 text-h3 font-bold text-ink">
              ยังดึงข้อมูลยอดขายรายสินค้าไม่ได้
            </h2>
            <p className="mt-1 text-body text-ink-2">
              {error instanceof Error
                ? error.message
                : 'กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง'}
            </p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-4 min-h-11 rounded-btn bg-primary px-4 text-body font-bold text-white transition duration-300 ease-out hover:bg-ink-2 active:scale-[0.98]"
            >
              ลองโหลดอีกครั้ง
            </button>
          </div>
        </section>
      ) : (
        <>
          <section
            className="mb-6 grid overflow-hidden rounded-card border border-line bg-surface lg:grid-cols-[1.2fr_0.8fr]"
            aria-label="สรุปยอดขายรายสินค้า"
          >
            <div className="relative min-h-[230px] overflow-hidden bg-surface px-5 py-6 text-ink sm:px-7 sm:py-7 lg:px-8">
              <div className="relative flex h-full flex-col">
                <div className="text-sm-label font-bold text-ink-2">
                  ยอดขายสุทธิ
                </div>
                {isLoading ? (
                  <div className="mt-5 h-12 w-56 animate-pulse rounded-card bg-line-2 sm:w-72" />
                ) : (
                  <div className="num mt-4 text-[38px] font-bold leading-none tracking-[-0.05em] sm:text-[48px]">
                    ฿{fmtAmount(data?.totalAmount ?? 0)}
                  </div>
                )}
                <div className="mt-auto flex flex-wrap items-end justify-between gap-4 pt-8">
                  <div>
                    <div
                      className={`num text-body font-bold ${
                        amtDelta >= 0 ? 'text-ok' : 'text-danger'
                      }`}
                    >
                      {amtDelta >= 0 ? '+' : '−'}
                      {Math.abs(amtDelta * 100).toFixed(1)}%
                    </div>
                    <div className="mt-1 text-sm-label text-ink-2">
                      เทียบกับช่วงก่อนหน้า
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="num text-lg font-bold">
                      {fmtInt(data?.totalQty ?? 0)}
                    </div>
                    <div className="text-sm-label text-ink-2">
                      หน่วยฐานที่ขาย
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 divide-x divide-y divide-line-2 [&>*:last-child]:col-span-2 lg:[&>*:last-child]:col-span-1 lg:divide-y-0 lg:grid-cols-1 lg:divide-x-0 lg:grid-rows-3">
              <SummaryMetric
                label="กำไรขั้นต้น"
                value={`฿${fmtAmount(data?.totalProfit ?? 0)}`}
                helper={
                  marginRate == null
                    ? 'ยังคำนวณ Margin ไม่ได้'
                    : `Margin ${(marginRate * 100).toFixed(1)}%`
                }
                loading={isLoading}
              />
              <SummaryMetric
                label="ต้นทุนขาย"
                value={`฿${fmtAmount(data?.totalCogs ?? 0)}`}
                helper="ต้นทุน ณ เวลาที่ขาย"
                loading={isLoading}
              />
              <SummaryMetric
                label="SKU ที่สร้างยอด"
                value={`${fmtInt(data?.activeSku ?? 0)} / ${fmtInt(data?.totalSku ?? 0)}`}
                helper={
                  data?.missingCostLineCount
                    ? `ต้นทุนไม่ครบ ${fmtInt(data.missingCostLineCount)} รายการ`
                    : `${(activeSkuRate * 100).toFixed(0)}% ของสินค้าทั้งหมด`
                }
                warning={Boolean(data?.missingCostLineCount)}
                loading={isLoading}
                className="col-span-2 lg:col-span-1"
              />
            </div>
          </section>

          <FilterBar>
            <div className="w-full">
              <div className="grid gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(360px,0.65fr)] xl:items-end">
                <div>
                  <div className="mb-3 flex items-baseline justify-between gap-3">
                    <div>
                      <h2
                        id="analysis-menu-title"
                        className="text-body font-bold text-ink"
                      >
                        เลือกมุมวิเคราะห์
                      </h2>
                      <p className="mt-0.5 text-sm-label text-ink-3">
                        ตารางและกราฟจะเรียงใหม่ตามค่าที่เลือก
                      </p>
                    </div>
                    <span className="hidden text-sm-label font-semibold text-ink-4 sm:inline">
                      จัดอันดับตาม
                    </span>
                  </div>
                  <div
                    className="grid grid-cols-3 gap-1 rounded-[12px] bg-bg-warm p-1.5"
                    role="group"
                    aria-label="จัดอันดับตาม"
                  >
                    {(['amount', 'profit', 'qty'] as Metric[]).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setMetric(m)}
                        aria-pressed={metric === m}
                        className={`min-h-12 rounded-[9px] px-2 text-body font-bold transition duration-300 ease-out active:scale-[0.98] sm:px-4 ${
                          metric === m
                            ? 'bg-surface text-primary shadow-[0_5px_16px_-10px_rgba(86,16,37,0.55)] ring-1 ring-line'
                            : 'text-ink-3 hover:bg-surface/60 hover:text-ink'
                        }`}
                      >
                        {m === 'amount'
                          ? 'ยอดขาย'
                          : m === 'profit'
                            ? 'กำไร'
                            : 'จำนวน'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="flex min-w-0 flex-col gap-2">
                    <span className="text-sm-label font-bold text-ink-2">
                      หมวดสินค้า
                    </span>
                    <Select
                      value={cat}
                      onChange={(e) => setCat(e.target.value)}
                      aria-label="หมวดสินค้า"
                    >
                      <option value="all">ทุกหมวด</option>
                      {cats.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </Select>
                  </label>
                  <label className="flex min-w-0 flex-col gap-2">
                    <span className="text-sm-label font-bold text-ink-2">
                      การเปลี่ยนแปลง
                    </span>
                    <Select
                      value={mover}
                      onChange={(e) => setMover(e.target.value as Mover)}
                      aria-label="มุมมอง"
                    >
                      <option value="all">ทุกทิศทาง</option>
                      <option value="up">ยอดกำลังโต</option>
                      <option value="down">ยอดกำลังลด</option>
                    </Select>
                  </label>
                </div>
              </div>
            </div>
          </FilterBar>

          <div className="mb-6 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1.3fr)_minmax(340px,0.7fr)]">
            <section className="rounded-[20px] border border-line-2 bg-surface p-4 sm:p-5">
              <div className="mb-2 flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm-label font-bold uppercase tracking-[0.12em] text-primary">
                    โครงสร้างยอดขาย
                  </div>
                  <h2 className="mt-1 text-h3 font-bold text-ink">
                    สินค้ากลุ่มไหนทำยอดส่วนใหญ่
                  </h2>
                </div>
                <span className="rounded-chip bg-bg-warm px-3 py-1 text-sm-label font-bold text-ink-3">
                  Pareto 80/20
                </span>
              </div>
              {isLoading ? (
                chartSkeleton
              ) : (
                <Suspense fallback={chartSkeleton}>
                  <ParetoChart
                    showAllLabels
                    data={pareto}
                    valueFormat={valueFmt}
                    tickFormat={tickFmt}
                  />
                </Suspense>
              )}
            </section>

            <section className="rounded-[20px] border border-line-2 bg-surface p-4 sm:p-5">
              <div className="mb-2">
                <div className="text-sm-label font-bold uppercase tracking-[0.12em] text-primary">
                  ผู้นำยอดขาย
                </div>
                <h2 className="mt-1 text-h3 font-bold text-ink">
                  8 อันดับตาม{metricLabel}
                </h2>
              </div>
              {isLoading ? (
                chartSkeleton
              ) : (
                <Suspense fallback={chartSkeleton}>
                  <BarCompare
                    data={topBars}
                    colorByIndex
                    valueFormat={valueFmt}
                    tickFormat={tickFmt}
                  />
                </Suspense>
              )}
            </section>
          </div>

          <section className="overflow-hidden rounded-[20px] border border-line-2 bg-surface">
            <div className="flex flex-col gap-2 border-b border-line-2 px-4 py-4 sm:flex-row sm:items-end sm:justify-between sm:px-5">
              <div>
                <div className="text-sm-label font-bold uppercase tracking-[0.12em] text-primary">
                  อันดับสินค้า
                </div>
                <h2 className="mt-1 text-h3 font-bold text-ink">
                  รายละเอียดรายสินค้า
                </h2>
              </div>
              {!isLoading && (
                <div className="text-sm-label text-ink-3">
                  พบ <b className="num text-ink">{fmtInt(visible.length)}</b>{' '}
                  รายการ · เลือกสินค้าเพื่อดูรายละเอียด
                </div>
              )}
            </div>

            {!ready || isLoading ? (
              <div className="space-y-3 p-5" aria-label="กำลังโหลดรายสินค้า">
                {[0, 1, 2, 3, 4].map((row) => (
                  <div
                    key={row}
                    className="h-12 animate-pulse rounded-[10px] bg-bg-warm"
                    style={{ animationDelay: `${row * 90}ms` }}
                  />
                ))}
              </div>
            ) : (
              <DataTable
                pageSize={Number.MAX_SAFE_INTEGER}
                mobileCards
                data={visible}
                onRowClick={(row) => setOpenId(row.productId)}
                emptyMessage="ไม่พบสินค้าที่ตรงกับตัวกรอง"
                columns={[
                  { id: 'rank', header: '#', cell: ({ row }) => row.index + 1 },
                  { accessorKey: 'name', header: 'สินค้า' },
                  {
                    id: 'category',
                    header: 'หมวด',
                    cell: ({ row }) =>
                      catName[row.original.categoryId] ??
                      row.original.categoryId,
                  },
                  {
                    id: 'qty',
                    header: 'จำนวนหน่วยฐาน',
                    cell: ({ row }) =>
                      `${fmtInt(row.original.qty)} ${row.original.baseUnitName}`,
                  },
                  {
                    accessorKey: 'amount',
                    header: 'ยอดขายสุทธิ',
                    cell: ({ row }) => fmtAmount(row.original.amount),
                  },
                  {
                    accessorKey: 'cogs',
                    header: 'ต้นทุน',
                    cell: ({ row }) => fmtAmount(row.original.cogs),
                  },
                  {
                    accessorKey: 'profit',
                    header: 'กำไร',
                    cell: ({ row }) => fmtAmount(row.original.profit),
                  },
                  {
                    accessorKey: 'margin',
                    header: 'Margin',
                    cell: ({ row }) =>
                      row.original.margin == null
                        ? '—'
                        : `${(row.original.margin * 100).toFixed(1)}%`,
                  },
                  {
                    id: 'share',
                    header: 'สัดส่วน',
                    cell: ({ row }) =>
                      `${(totalForShare ? (shareValue(metric, valOf(row.original)) / totalForShare) * 100 : 0).toFixed(1)}%`,
                  },
                  {
                    id: 'delta',
                    header: 'เทียบงวดก่อน',
                    cell: ({ row }) =>
                      `${(deltaFrac(valOf(row.original), prevOf(row.original)) * 100).toFixed(1)}%`,
                  },
                ]}
              />
            )}
          </section>
          <Modal
            open={!!openId}
            variant="drawer"
            className="!my-0"
            title="รายละเอียดรายสินค้า"
            onClose={() => setOpenId(null)}
          >
            {openId && (
              <ProductDrilldown
                productId={openId}
                name={
                  visible.find((row) => row.productId === openId)?.name ?? ''
                }
                range={range}
                scope={scope}
              />
            )}
          </Modal>
        </>
      )}
    </div>
  );
}

function SummaryMetric({
  label,
  value,
  helper,
  warning = false,
  loading = false,
  className = '',
}: {
  label: string;
  value: string;
  helper: string;
  warning?: boolean;
  loading?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`flex min-h-[108px] flex-col justify-center px-4 py-4 sm:px-6 ${className}`}
    >
      <div className="text-sm-label font-bold uppercase tracking-[0.1em] text-ink-3">
        {label}
      </div>
      {loading ? (
        <div className="mt-2 h-7 w-32 animate-pulse rounded-card bg-bg-warm" />
      ) : (
        <div className="num mt-1 text-[22px] font-bold leading-tight tracking-[-0.03em] text-ink">
          {value}
        </div>
      )}
      <div
        className={`mt-1 text-sm-label ${
          warning ? 'font-bold text-warn' : 'text-ink-3'
        }`}
      >
        {helper}
      </div>
    </div>
  );
}

function ProductDrilldown({
  productId,
  name,
  range,
  scope,
}: {
  productId: string;
  name: string;
  range: { fromDay: number; toDay: number };
  scope: { branchId: string | null; allowedBranchIds?: string[] };
}) {
  const { data, isLoading } = useQuery({
    queryKey: [
      'product-detail',
      productId,
      scope.branchId,
      scope.allowedBranchIds,
      range.fromDay,
      range.toDay,
    ],
    queryFn: () => getProductDetail(productId, scope, range),
  });
  if (isLoading || !data)
    return <div className="h-40 m-4 animate-pulse bg-bg-warm rounded-card" />;
  return (
    <div className="min-w-0 space-y-5">
      <div className="flex items-start justify-between gap-3 border-b border-line pb-3">
        <div className="min-w-0">
          <div className="text-sm-label font-bold uppercase tracking-[0.1em] text-primary">
            รายละเอียดสินค้าที่เลือก
          </div>
          <h3 className="mt-1 break-words text-body font-bold text-ink">
            {name}
          </h3>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 [&>*:last-child]:col-span-2">
        {[
          {
            label: `ขายเทียบ ${data.summary.baseUnitName}`,
            value: fmtInt(data.summary.baseQuantity),
          },
          {
            label: 'ยอดขายสุทธิ',
            value: `฿${fmtAmount(data.summary.netAmount)}`,
          },
          { label: 'ต้นทุนขาย', value: `฿${fmtAmount(data.summary.cogs)}` },
          { label: 'กำไรขั้นต้น', value: `฿${fmtAmount(data.summary.profit)}` },
          {
            label: 'Margin',
            value:
              data.summary.margin == null
                ? '—'
                : `${(data.summary.margin * 100).toFixed(1)}%`,
          },
        ].map((item) => (
          <div
            key={item.label}
            className="min-w-0 rounded-card border border-line bg-surface p-3"
          >
            <div className="text-sm-label text-ink-3">{item.label}</div>
            <div className="num mt-1 break-words text-base font-bold text-ink sm:text-lg">
              {item.value}
            </div>
          </div>
        ))}
      </div>

      {data.summary.missingCostLineCount > 0 && (
        <div className="rounded-card border border-warn bg-warn-light px-3 py-2 text-sm text-warn">
          มี {fmtInt(data.summary.missingCostLineCount)} รายการขายที่ไม่มีต้นทุน
          กำไรที่แสดงอาจสูงกว่าความเป็นจริง
        </div>
      )}

      <div className="overflow-hidden rounded-card border border-line bg-surface md:overflow-x-auto">
        <div className="px-4 py-3 text-sm-label font-bold text-ink-3">
          ขายแยกตามหน่วย — {name}
        </div>
        <table className="bo-table bo-table-mobile-cards">
          <thead>
            <tr>
              <th>หน่วยขาย</th>
              <th className="bo-th-num">จำนวนที่ขาย</th>
              <th className="bo-th-num">อัตราแปลง</th>
              <th className="bo-th-num">เทียบหน่วยฐาน</th>
              <th className="bo-th-num">ยอดขายสุทธิ</th>
              <th className="bo-th-num">ต้นทุน</th>
              <th className="bo-th-num">กำไร</th>
              <th className="bo-th-num">Margin</th>
            </tr>
          </thead>
          <tbody>
            {data.byUnit.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="bo-table-empty text-center text-ink-3 py-6"
                >
                  ไม่มียอดขายในช่วงเวลานี้
                </td>
              </tr>
            ) : (
              data.byUnit.map((unit) => (
                <tr key={`${unit.unitId}:${unit.unitName}:${unit.ratio}`}>
                  <td data-label="หน่วยขาย" className="font-semibold">
                    {unit.unitName}
                  </td>
                  <td data-label="จำนวนที่ขาย" className="bo-td-num num">
                    {fmtInt(unit.quantity)}
                  </td>
                  <td data-label="อัตราแปลง" className="bo-td-num num">
                    1 {unit.unitName} = {fmtInt(unit.ratio)}{' '}
                    {data.summary.baseUnitName}
                  </td>
                  <td data-label="เทียบหน่วยฐาน" className="bo-td-num num">
                    {fmtInt(unit.baseQuantity)}
                  </td>
                  <td data-label="ยอดขายสุทธิ" className="bo-td-num num">
                    {fmtAmount(unit.netAmount)}
                  </td>
                  <td data-label="ต้นทุน" className="bo-td-num num">
                    {fmtAmount(unit.cogs)}
                  </td>
                  <td
                    data-label="กำไร"
                    className={`bo-td-num num font-semibold ${unit.profit < 0 ? 'text-danger' : 'text-ok'}`}
                  >
                    {fmtAmount(unit.profit)}
                  </td>
                  <td data-label="Margin" className="bo-td-num num">
                    {unit.margin == null
                      ? '—'
                      : `${(unit.margin * 100).toFixed(1)}%`}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div>
          <div className="text-sm-label font-bold text-ink-3 mb-2">
            แนวโน้มยอดขายสุทธิรายวัน
          </div>
          <Suspense fallback={chartSkeleton}>
            <LineTrend
              data={data.series}
              height={CHART_H.sm}
              valueFormat={fmtBahtShort}
              tickFormat={fmtSatangCompact}
            />
          </Suspense>
        </div>
        <div>
          <div className="text-sm-label font-bold text-ink-3 mb-2">
            แยกตามสาขา
          </div>
          <Suspense fallback={chartSkeleton}>
            <BarCompare
              data={data.byBranch.map((b) => ({
                label: b.branchShort,
                value: b.qty,
              }))}
              height={CHART_H.sm}
              colorByIndex
            />
          </Suspense>
          <div className="mt-3 overflow-hidden rounded-card border border-line bg-surface md:overflow-x-auto">
            <table className="bo-table bo-table-mobile-cards">
              <thead>
                <tr>
                  <th>สาขา</th>
                  <th className="bo-th-num">หน่วยฐาน</th>
                  <th className="bo-th-num">ยอดขายสุทธิ</th>
                  <th className="bo-th-num">ต้นทุน</th>
                  <th className="bo-th-num">กำไร</th>
                </tr>
              </thead>
              <tbody>
                {data.byBranch.map((branch) => (
                  <tr key={branch.branchId}>
                    <td data-label="สาขา" className="font-semibold">
                      {branch.branchShort}
                    </td>
                    <td data-label="หน่วยฐาน" className="bo-td-num num">
                      {fmtInt(branch.qty)}
                    </td>
                    <td data-label="ยอดขายสุทธิ" className="bo-td-num num">
                      {fmtAmount(branch.amount)}
                    </td>
                    <td data-label="ต้นทุน" className="bo-td-num num">
                      {fmtAmount(branch.cogs)}
                    </td>
                    <td
                      data-label="กำไร"
                      className={`bo-td-num num font-semibold ${branch.profit < 0 ? 'text-danger' : 'text-ok'}`}
                    >
                      {fmtAmount(branch.profit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
